import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, Lock, Loader2, CheckCircle, 
  Eye, EyeOff, KeyRound, AlertCircle, ShieldCheck
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const res = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(res.data.valid);
        setUserEmail(res.data.email);
        setUserName(res.data.user_name);
      } catch (err) {
        setTokenValid(false);
      }
      setVerifying(false);
      setLoading(false);
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('A palavra-passe deve ter pelo menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('As palavras-passe não coincidem');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Palavra-passe alterada com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar palavra-passe');
    }
    setSubmitting(false);
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sonangol-red to-red-700 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/80">A verificar link...</p>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sonangol-red to-red-700 flex flex-col">
        <div className="pt-6 pb-4 px-4">
          <Link 
            to="/auth" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Voltar</span>
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-xl">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-barlow text-2xl font-bold text-zinc-800 mb-2">
              Link Inválido
            </h2>
            <p className="text-zinc-600 mb-6">
              Este link de recuperação é inválido ou já expirou. 
              Solicite um novo link para redefinir a sua palavra-passe.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full h-12 bg-sonangol-red hover:bg-red-700">
                Solicitar Novo Link
              </Button>
            </Link>
            <Link 
              to="/auth" 
              className="block mt-4 text-sonangol-red hover:underline font-medium"
            >
              ← Voltar ao login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sonangol-red to-red-700 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="font-barlow text-2xl font-bold text-zinc-800 mb-2">
              Palavra-passe Alterada!
            </h2>
            <p className="text-zinc-600 mb-6">
              A sua palavra-passe foi alterada com sucesso. 
              Já pode iniciar sessão com a nova palavra-passe.
            </p>
            <Button 
              className="w-full h-12 bg-sonangol-red hover:bg-red-700"
              onClick={() => navigate('/auth')}
              data-testid="go-to-login-btn"
            >
              Iniciar Sessão
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sonangol-red to-red-700 flex flex-col">
      {/* Header */}
      <div className="pt-6 pb-4 px-4">
        <Link 
          to="/auth" 
          className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Voltar</span>
        </Link>
      </div>

      <div className="px-4 pb-8">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg mb-4">
            <ShieldCheck className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-barlow text-3xl font-bold text-white uppercase tracking-tight">
            Nova Palavra-passe
          </h1>
          {userName && (
            <p className="text-white/80 text-sm mt-2 font-manrope">
              Olá, {userName}!
            </p>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 bg-white rounded-t-3xl px-6 py-8">
        <div className="max-w-md mx-auto">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-blue-800 text-sm">
              <strong>Email:</strong> {userEmail}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-700 font-medium">
                Nova palavra-passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Mínimo 6 caracteres"
                  className="h-14 pl-12 pr-12 bg-zinc-50 border-zinc-200 rounded-xl text-zinc-800"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-700 font-medium">
                Confirmar palavra-passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Repita a palavra-passe"
                  className="h-14 pl-12 bg-zinc-50 border-zinc-200 rounded-xl text-zinc-800"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  As palavras-passe não coincidem
                </p>
              )}
            </div>

            {/* Password requirements */}
            <div className="bg-zinc-50 rounded-xl p-4">
              <p className="text-zinc-600 text-sm font-medium mb-2">Requisitos:</p>
              <ul className="space-y-1">
                <li className={`text-sm flex items-center gap-2 ${password.length >= 6 ? 'text-green-600' : 'text-zinc-400'}`}>
                  <CheckCircle className={`w-4 h-4 ${password.length >= 6 ? 'text-green-600' : 'text-zinc-300'}`} />
                  Pelo menos 6 caracteres
                </li>
                <li className={`text-sm flex items-center gap-2 ${password === confirmPassword && password.length > 0 ? 'text-green-600' : 'text-zinc-400'}`}>
                  <CheckCircle className={`w-4 h-4 ${password === confirmPassword && password.length > 0 ? 'text-green-600' : 'text-zinc-300'}`} />
                  Palavras-passe coincidem
                </li>
              </ul>
            </div>

            <Button 
              type="submit" 
              className="w-full h-14 bg-sonangol-red hover:bg-red-700 font-barlow font-bold uppercase tracking-wider text-lg rounded-xl shadow-lg"
              disabled={submitting || password.length < 6 || password !== confirmPassword}
              data-testid="reset-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  A alterar...
                </>
              ) : (
                <>
                  <KeyRound className="w-5 h-5 mr-2" />
                  Alterar Palavra-passe
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
